import React, { useEffect, useMemo, useState } from "react";
import Layout from "../Layout";
import {
  Button,
  DatePickerComp,
  FromToDate,
  Input,
  Select,
  Switchi,
  TimePickerComp,
} from "../components/Form";
import { NotificationTable, Transactiontable } from "../components/Tables";
import { sortsDatas, transactionData } from "../components/Datas";
import { BiChevronDown, BiPlus, BiTime } from "react-icons/bi";
import {
  MdEditCalendar,
  MdFilterList,
  MdOutlineCalendarMonth,
  MdOutlineCloudDownload,
} from "react-icons/md";
import { toast } from "react-hot-toast";
import { BsCalendarMonth } from "react-icons/bs";
import { Link, useNavigate } from "react-router-dom";
import ThreeColumRow from "../components/ThreeColumRow";
import TwoColumRow from "../components/TwoColumRow";
import { HiOutlineCheckCircle } from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import {
  createNotification,
  deleteNotification,
  getAllNotifications,
  getNotifications,
} from "../redux/features/notificationSlice";
import NewPatientFast from "../components/Modals/NewPatientFast";
import { getAllPatients, getPatients } from "../redux/features/patientSlice";
import SearchBar from "../components/SearchBar";
import ConfirmationAlert from "../components/ConfirmationAlert";
import { useForm } from "react-hook-form";

// Cada recordatorio trae SU PROPIA regla de repetición (no una configuración global).
const MODOS_AVISO = [
  { id: 1, name: "Una vez", value: "una_vez" },
  { id: 2, name: "Un día antes", value: "dia_antes" },
  { id: 3, name: "Todos los días", value: "diaria" },
];

// Cómo se le muestra a la doctora dentro de la app.
const PRESENTACIONES = [
  { id: 1, name: "Solo campanita", value: "campanita" },
  { id: 2, name: "Banner arriba", value: "banner" },
  { id: 3, name: "Ventana emergente", value: "popup" },
];

function Notifications() {
  const [edit, setEdit] = useState(null);
  const [openPatient, setOpenPatient] = useState(false);
  const [selected, setSelected] = useState(false);
  const [fecha, setFecha] = useState(new Date());
  const [startTime, setStartTime] = useState(fecha);
  // Por defecto el recordatorio se ve en la app; el email es opcional.
  const [sistema, setSistema] = useState(true);
  const [mail, setMail] = useState(false);
  const [frecuencia, setFrecuencia] = useState(MODOS_AVISO[0]);
  const [presentacion, setPresentacion] = useState(PRESENTACIONES[0]);
  const [fechaFin, setFechaFin] = useState(new Date());

  const notifications = useSelector(getAllNotifications);
  const patients = useSelector(getAllPatients);

  // Esta pantalla administra los RECORDATORIOS manuales. Aunque el backend traiga todo (en producción
  // eran ~38 mil notificaciones automáticas de citas), acá filtramos y capamos en el cliente para no
  // renderizar miles de filas: eso hacía que cada clic en un selector (hora, fecha, modo) congelara la
  // página al re-renderizar toda la tabla. useMemo evita recalcularlo en cada tecla.
  const notificacionesTabla = useMemo(
    () =>
      (notifications ?? [])
        .filter((n) => n?.tipo !== "automatico")
        .slice()
        .sort((a, b) => new Date(b.fecha) - new Date(a.fecha))
        .slice(0, 200),
    [notifications]
  );

  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  // Carga una sola vez al entrar. Antes dependía de notifications.length: cada alta/baja volvía a
  // pedir TODOS los pacientes (endpoint lento), y eso hacía que la pantalla se congelara.
  // Tras crear o dar de baja ya se refresca la lista explícitamente más abajo.
  useEffect(() => {
    dispatch(getPatients());
    dispatch(getNotifications());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const editNotification = (item) => {
    setEdit(item);
    setFecha(new Date(item.fecha));
    setSelected(item.patient);
    setSistema(item.sistema);
    setStartTime(new Date(item.fecha));
    setMail(item.mail);
    setValue("motivo", item.motivo);
    setFrecuencia(
      MODOS_AVISO.find((m) => m.value === item.frecuencia) ?? MODOS_AVISO[0]
    );
    setPresentacion(
      PRESENTACIONES.find((p) => p.value === item.presentacion) ??
        PRESENTACIONES[0]
    );
    setFechaFin(item.fecha_fin ? new Date(item.fecha_fin) : new Date());
  };
  // preview
  const bajaNotification = (id) => {
    ConfirmationAlert({
      title: `Baja Notificación`,
      text: `Esta seguro de dar de baja la Notificación?`,
      onAccept: async () => {
        const resDelete = await dispatch(deleteNotification(id));
        if (resDelete.error) {
          toast.error(
            "Ocurrio un error al dar baja la notificación: " + resDelete.error,
            {
              duration: 5000,
            }
          );
        } else {
          dispatch(getNotifications());
          toast.success(
            "Se realizo la baja de la notificación correctamente!",
            { duration: 5000 }
          );
        }
      },
    });
  };

  const onSubmit = (body) => {
    {
      ConfirmationAlert({
        title: `${edit?.id ? "Editar" : "Registrar"} Recordatorio`,
        text: `Esta seguro de ${
          edit?.id ? "editar" : "registrar"
        } el Recordatorio?`,
        onAccept: async () => {
          // Se manda la fecha/hora como texto local "YYYY-MM-DD HH:mm:ss" (sin .toISOString()).
          // Antes toISOString() la pasaba a UTC (La Paz = UTC-4), por eso 7:56 PM se guardaba como
          // 11:56 PM. El backend (APP_TIMEZONE=America/La_Paz) la interpreta como hora local.
          const pad = (n) => String(n).padStart(2, "0");
          const soloFecha = (d) =>
            `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
          const auxFecha = `${soloFecha(fecha)} ${pad(
            startTime.getHours()
          )}:${pad(startTime.getMinutes())}:00`;
          body = {
            ...body,
            fecha: auxFecha,
            estado: edit?.estado ?? "pendiente",
            sistema,
            mail,
            tipo: "manual",
            // El paciente es OPCIONAL: sin paciente queda como recordatorio general del consultorio.
            patient_id: selected?.id ?? null,
            frecuencia: frecuencia.value,
            presentacion: presentacion.value,
            // "hasta" solo aplica cuando se repite todos los días. Se manda como fecha local para
            // evitar que la conversión a UTC la corra un día.
            fecha_fin:
              frecuencia.value === "diaria" ? soloFecha(fechaFin) : null,
          };
          if (edit?.id) {
            body.id = edit.id;
          }

          const res = await dispatch(createNotification(body));
          if (res.error) {
            // res.payload trae el mensaje real del backend (rejectWithValue); res.error es un objeto
            // y antes se concatenaba tal cual, mostrando "[object Object]".
            const detalle =
              res.payload ?? res.error?.message ?? "Error desconocido";
            toast.error(
              `Ocurrio un error al ${
                edit?.id ? "editar" : "registrar"
              } la notificación: ${detalle}`,
              {
                duration: 8000,
              }
            );
          } else {
            setEdit(null);
            dispatch(getNotifications());
            toast.success(
              `Notificación ${edit?.id ? "editada" : "creada"} correctamente!`,
              { duration: 5000 }
            );
            setSelected(null);
            setFecha(new Date());
            setStartTime(new Date());
            setSistema(true);
            setMail(false);
            setFrecuencia(MODOS_AVISO[0]);
            setPresentacion(PRESENTACIONES[0]);
            setFechaFin(new Date());
            setValue("motivo", "");
          }
        },
      });
    }
  };
  return (
    <Layout>
      <h1 className="text-xl font-semibold">Notificaciones</h1>

      {/* datas */}
      <div
        data-aos="fade-up"
        data-aos-duration="1000"
        data-aos-delay="10"
        data-aos-offset="200"
        className="bg-white my-8 rounded-xl border-[1px] border-border p-5"
      >
        <div className="border rounded-lg p-5">
          <h2 className=" font-semibold text-md py-5">
            {`${edit?.id ? "Editar" : "Nueva"}`} Notificación
          </h2>
          <ThreeColumRow>
            <div className="gap-4 w-full items-center">
              {openPatient && (
                <NewPatientFast
                  open={openPatient}
                  setOpen={setOpenPatient}
                  setPatient={setSelected}
                />
              )}
              <div className="z-50">
                <label className={"text-black text-sm block"}>
                  Paciente: <span className="text-textGray">(opcional)</span>
                </label>
                <SearchBar
                  key={selected ?? 1}
                  placeholder="Escriba nombre o CI para buscar al paciente"
                  data={patients?.data}
                  setSelected={setSelected}
                  value={
                    selected
                      ? `${
                          selected.persona.ci.length > 10
                            ? selected.persona.ci.slice(0, 8)
                            : selected.persona.ci
                        } - ${selected.persona.nombre} ${
                          selected.persona.apellidos
                        }`
                      : ""
                  }
                  setOpen={setOpenPatient}
                />
              </div>
            </div>

            <TwoColumRow>
              <DatePickerComp
                label="Fecha"
                startDate={fecha}
                onChange={(date) => setFecha(date)}
              />
              <div>
                <TimePickerComp
                  label="Hora"
                  startDate={startTime}
                  onChange={(date) => setStartTime(date)}
                />
              </div>
            </TwoColumRow>
            <div className="mt-8">
              <Button
                label={edit?.id ? "Guardar Cambios" : "Registrar"}
                Icon={HiOutlineCheckCircle}
                onClick={handleSubmit(onSubmit)}
              />
            </div>
          </ThreeColumRow>
          <TwoColumRow>
            <div className="w-1/2">
              <Input
                label="Motivo"
                type="text"
                register={register("motivo", {
                  required: "Este campo es requerido",
                })}
                error={errors.motivo}
                color={true}
              />
            </div>
            <div className="flex gap-5 p-5 justify-center">
              <TwoColumRow>
                <h2>Tipo de Notificación</h2>
                <TwoColumRow>
                  <Switchi checked={mail} label="Email" onChange={setMail} />
                  <Switchi
                    checked={sistema}
                    label="Web"
                    onChange={setSistema}
                  />
                </TwoColumRow>
              </TwoColumRow>
            </div>
          </TwoColumRow>

          {/* Reglas del recordatorio: cada uno tiene la suya */}
          <ThreeColumRow>
            <div className="flex w-full flex-col gap-2">
              <label className="text-black text-sm">Cuándo avisar</label>
              <Select
                selectedPerson={frecuencia}
                setSelectedPerson={setFrecuencia}
                datas={MODOS_AVISO}
              >
                <div className="w-full flex-btn text-textGray text-sm p-4 border border-border font-light rounded-lg">
                  {frecuencia?.name} <BiChevronDown className="text-xl" />
                </div>
              </Select>
              <span className="text-xs text-textGray">
                {frecuencia?.value === "una_vez" &&
                  "Aparece desde la fecha y hora elegidas y queda hasta que lo canceles."}
                {frecuencia?.value === "dia_antes" &&
                  "Poné la fecha del evento: el aviso sale el día anterior."}
                {frecuencia?.value === "diaria" &&
                  "Aparece todos los días, desde la fecha elegida hasta la fecha de fin."}
              </span>
            </div>

            {/* El "hasta" solo tiene sentido si se repite todos los días */}
            {frecuencia?.value === "diaria" ? (
              <DatePickerComp
                label="Repetir hasta"
                startDate={fechaFin}
                onChange={(date) => setFechaFin(date)}
              />
            ) : (
              <div />
            )}

            <div className="flex w-full flex-col gap-2">
              <label className="text-black text-sm">Cómo mostrarlo</label>
              <Select
                selectedPerson={presentacion}
                setSelectedPerson={setPresentacion}
                datas={PRESENTACIONES}
              >
                <div className="w-full flex-btn text-textGray text-sm p-4 border border-border font-light rounded-lg">
                  {presentacion?.name} <BiChevronDown className="text-xl" />
                </div>
              </Select>
              <span className="text-xs text-textGray">
                El banner y la ventana emergente se cierran con la X.
              </span>
            </div>
          </ThreeColumRow>
        </div>
        <div className="mt-8 w-full overflow-x-scroll">
          <NotificationTable
            notifications={notificacionesTabla}
            action={true}
            functions={{
              edit: editNotification,
              baja: bajaNotification,
            }}
          />
        </div>
      </div>
    </Layout>
  );
}

export default Notifications;
